import {find, isString} from 'lodash';
import React, {Children, ReactNode} from 'react';

import {escapeRegex, isBasicLink} from '../../../../libs/shared/core/src/helpers/utilities/stringHelpers';
import {CommentModel} from '../../../../src/models/api/commentModel';
import {
  CommentUnfurlModel,
  CommentUnfurlTypesEnum,
  TagCommentUnfurlModel,
} from '../../../../src/models/api/commentUnfurlModel';
import {DecoratedComponent} from '../markdownDecoratedComponents';
import {NodeTransform} from '../markdownFormatterTypes';

export interface UnfurlTransformComponentMap {
  TagUnfurlWrapper: DecoratedComponent<{unfurl: TagCommentUnfurlModel; comment: CommentModel}>;
  DefaultUnfurlWrapper: DecoratedComponent<{children: ReactNode}>;
  UnfurlErrorWrapper: DecoratedComponent<{children: ReactNode; unfurl: CommentUnfurlModel}>;
}

interface TransformProps {
  comment: CommentModel | null;
  components: UnfurlTransformComponentMap;
}

export function commonMakeMarkdownTransformUnfurls(props: TransformProps): NodeTransform {
  const {comment, components} = props;
  if (!comment) {
    return (node) => node;
  }
  const {unfurls} = comment;
  const {TagUnfurlWrapper, DefaultUnfurlWrapper, UnfurlErrorWrapper} = components;

  if (unfurls.length === 0) {
    return (node) => node;
  }

  const renderUnfurl = (unfurl: CommentUnfurlModel) => {
    if (unfurl.type === CommentUnfurlTypesEnum.TAG) {
      return <TagUnfurlWrapper unfurl={unfurl} comment={comment} />;
    }

    return <DefaultUnfurlWrapper>{unfurl.token}</DefaultUnfurlWrapper>;
  };

  const unfurlsRegexParts = unfurls.map((unfurl) => escapeRegex(unfurl.token)).join('|');
  const unfurlsRegex = new RegExp(`(${unfurlsRegexParts})`);
  return (node) =>
    Children.map(node, (child) => {
      // We only process strings.
      if (!isString(child)) {
        return child;
      }

      // Split the string by all unfurl tokens in this comment.
      return child.split(unfurlsRegex).map((part) => {
        const unfurl = find(unfurls, {token: part});
        if (!unfurl) {
          return part;
        }

        // Only apply style/tooltip to id unfurls, not link unfurls
        if (isBasicLink(part)) {
          return part;
        }

        const renderedUnfurl = renderUnfurl(unfurl);
        if (unfurl.error) {
          return <UnfurlErrorWrapper unfurl={unfurl}>{renderedUnfurl}</UnfurlErrorWrapper>;
        }

        return renderedUnfurl;
      });
    });
}
